import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';
import type { User } from '@/types/directory/User';

import {
    ANONYMOUS_MINIMUM_REFUSAL,
    feedback360SettingsSchema,
    meetsAnonymousMinimum,
} from '@/constants/review/FeedbackPrivacy';

export const AssignReviewerFormFields = {
    reviewerAssignments: 'reviewerAssignments',
    feedbackPrivacy: 'feedbackPrivacy',
    commentRepliesEnabled: 'commentRepliesEnabled',
} as const;

const VALIDATION_ERROR_NO_REVIEWEES: TranslationKey =
    'evaluation360AssignReviewers.validation.noReviewees';
const VALIDATION_ERROR_MIN_REVIEWERS: TranslationKey =
    'evaluation360AssignReviewers.validation.minReviewers';

export type ReviewerAssignment = {
    revieweeId: string;
    reviewers: User[];
    reviewAudienceAssignmentId: string;
};

export const assignReviewerSchema = feedback360SettingsSchema
    .extend({
        reviewerAssignments: z
            .array(
                z.object({
                    revieweeId: z.string(),
                    reviewers: z.array(z.any()),
                    reviewAudienceAssignmentId: z.string(),
                }),
            )
            .refine((assignments) => assignments.length > 0, {
                message: VALIDATION_ERROR_NO_REVIEWEES,
            })
            .refine(
                (assignments) =>
                    assignments.every((assignment) => assignment.reviewers.length >= 1) &&
                    assignments.length > 0,
                {
                    message: VALIDATION_ERROR_MIN_REVIEWERS,
                },
            ),
    })
    .refine(
        ({ feedbackPrivacy, reviewerAssignments }) =>
            reviewerAssignments.every((assignment) =>
                meetsAnonymousMinimum({
                    feedbackPrivacy,
                    respondentCount: assignment.reviewers.length,
                }),
            ),
        {
            message: ANONYMOUS_MINIMUM_REFUSAL.messageKey,
            path: [AssignReviewerFormFields.reviewerAssignments],
        },
    );

export type AssignReviewerFormData = z.infer<typeof assignReviewerSchema>;
